import { Controller } from "@hotwired/stimulus";
// Stimulus controller for the Reporting dashboard.

// Renders the published Databricks dashboard via external embedding: fetches a server-minted scoped
// token, loads the Databricks SDK, and initializes it into the container.
//
// One-shot render. The scoped token lives ~1h; when it expires the embed stops working and the user
// must reload the page (which re-mints). Automatic token refresh is intentionally not implemented —
// it depends on the SDK's expiry-callback behavior, which can't be verified without a live Databricks
// tenant. Tracked as a follow-up alongside the Databricks-side setup.
export default class extends Controller {
  static targets = ["container"];

  connect() {
    this.connected = true;
    this.render();
  }

  disconnect() {
    this.connected = false;
    this.dashboard?.destroy();
    this.dashboard = null;
  }

  async render() {
    this.containerTarget.setAttribute("aria-busy", "true");

    const config = await this.fetchEmbedConfig();
    if (!this.connected || !this.hasContainerTarget) return;
    if (!config) {
      this.showUnavailable();
      return;
    }

    try {
      const { DatabricksDashboard } = await import("@databricks/aibi-client");
      if (!this.connected || !this.hasContainerTarget) return;

      this.dashboard = new DatabricksDashboard({
        instanceUrl: config.instance_url,
        workspaceId: config.workspace_id,
        dashboardId: config.dashboard_id,
        token: config.token,
        container: this.containerTarget,
        config: { version: 1 },
      });
      await this.dashboard.initialize();
      this.containerTarget.removeAttribute("aria-busy");
    } catch (error) {
      console.error("Databricks dashboard failed to render", error);
      this.showUnavailable();
    }
  }

  async fetchEmbedConfig() {
    try {
      const response = await fetch("/reporting/token", {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        if (response.status !== 503) {
          console.error(`Reporting token request returned ${response.status}`);
        }
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error("Reporting token request failed", error);
      return null;
    }
  }

  showUnavailable() {
    this.containerTarget.removeAttribute("aria-busy");
    this.containerTarget.innerHTML =
      '<div class="alert alert-warning" role="alert">The reporting dashboard is currently unavailable. Try reloading the page.</div>';
  }
}
